import { message } from 'antd'
import { getAuth, signOut } from 'firebase/auth'
import React, { useCallback, useEffect, useRef } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

const SESSION_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes
const SESSION_START_KEY = 'sessionStartTime'

const ProtectedRoute = ({ children }) => {
  const auth = getAuth()
  const { currentUser } = auth
  const navigate = useNavigate()
  const activityTimeoutRef = useRef(null)

  const handleSessionExpired = useCallback(async () => {
    try {
      await signOut(auth)
      localStorage.removeItem(SESSION_START_KEY)
      localStorage.removeItem('sessionId')
      message.warning('Session expired. Please log in again.')
      navigate('/login')
    } catch (err) {
      console.error('Logout error:', err)
      navigate('/login')
    }
  }, [auth, navigate])

  const resetActivityTimer = useCallback(() => {
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current)
    }
    activityTimeoutRef.current = setTimeout(handleSessionExpired, SESSION_TIMEOUT_MS)
  }, [handleSessionExpired])

  useEffect(() => {
    if (!currentUser) return

    // Check if session has already expired
    const sessionStart = localStorage.getItem(SESSION_START_KEY)
    if (sessionStart) {
      const elapsed = Date.now() - parseInt(sessionStart, 10)
      if (elapsed >= SESSION_TIMEOUT_MS) {
        handleSessionExpired()
        return
      }
    }

    // Start activity timer
    resetActivityTimer()

    // Listen for user activity
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']
    const handleActivity = () => {
      localStorage.setItem(SESSION_START_KEY, Date.now().toString())
      resetActivityTimer()
    }

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current)
      }
    }
  }, [currentUser, resetActivityTimer, handleSessionExpired])

  if (!currentUser) {
    return <Navigate to="/login" />
  }

  return children
}

export default ProtectedRoute
